import { useRouter } from 'expo-router';
import { EyeOff, X } from 'lucide-react-native';
import { Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { ROUTES } from '@/constants/routes';
import { useAccountSwitcher } from '@/hooks/useAccountSwitcher';
import { useIsActingAsBannerVisible } from '@/hooks/useIsActingAsBannerVisible';
import { useActingAsBannerStore } from '@/services/stores/actingAsBannerStore';
import { MINIMUM_TARGET_SIZE } from '@/util/accessibleTouchTarget';

/** Content height of the banner without safe area inset: the 48px touch target its buttons fill. */
export const ACTING_AS_BANNER_CONTENT_HEIGHT = MINIMUM_TARGET_SIZE;

/**
 * Banner displayed when a user is acting as a support account.
 * Shows the name of the account being impersonated and allows
 * the user to switch back to themselves, or hide the banner for the
 * rest of the session while continuing to act as that account.
 *
 * Includes safe area padding so the highlight color extends
 * behind the notch/status bar.
 *
 * @returns The banner element when impersonating and not hidden, otherwise null.
 */
export function ActingAsBanner() {
  const { top } = useSafeAreaInsets();

  const router = useRouter();

  const { actAsUserId, currentAccount, switchToSelf } = useAccountSwitcher();

  const isVisible = useIsActingAsBannerVisible();

  const hide = useActingAsBannerStore((state) => state.hide);

  if (!isVisible) {
    return null;
  }

  return (
    <Box className="border-0 bg-warning-500" style={{ paddingTop: top }}>
      <HStack className="min-h-[48px] items-center justify-between pl-4">
        <Text className="flex-1 font-medium text-sm text-white">
          Acting as: {currentAccount?.label ?? 'Support Account'}
        </Text>
        <Pressable
          onPress={() => {
            if (actAsUserId) {
              hide(actAsUserId);
            }
          }}
          className="items-center justify-center self-stretch px-4"
          accessibilityLabel="Hide acting as banner"
          accessibilityRole="button">
          <Icon as={EyeOff} size="sm" className="text-white" />
        </Pressable>
        <Pressable
          onPress={() => {
            switchToSelf();

            router.replace(ROUTES.ACCOUNT);
          }}
          className="items-center justify-center self-stretch px-4"
          accessibilityLabel="Stop acting as support account"
          accessibilityRole="button">
          <Icon as={X} size="sm" className="text-white" />
        </Pressable>
      </HStack>
    </Box>
  );
}
